import React from 'react';
import { 
  Container, Typography, Box, Paper, Accordion, AccordionSummary, 
  AccordionDetails, Button, Divider, IconButton
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const HelpCenter = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/dashboard');
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          Help Center
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 2 }}>
          Dashboard Help Documents
        </Typography>

        <Divider sx={{ mb: 3 }} />

        {/* Navigating the Dashboard */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">1. Navigating the Dashboard</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The dashboard is the central hub for managing your missed call text back application. 
              It provides access to all the key features, including viewing and editing Q&A pairs, 
              testing the AI, viewing customer conversations, and adding new Q&A pairs.
            </Typography>
            <Typography>
              Use the navigation menu at the top or side of the dashboard to access these features.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Viewing and Editing Q&A Pairs */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">2. Viewing and Editing Q&A Pairs</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              To view your current Q&A pairs, navigate to the 'Q&A Pairs' section on the dashboard. 
              Here, you will see a list of all the questions and answers you have added to train the AI.
            </Typography>
            <Typography paragraph>
              To edit an existing Q&A pair, follow these steps:
            </Typography>
            <Typography component="ol" sx={{ pl: 2 }}>
              <li>Click on the Q&A pair you want to edit.</li>
              <li>Make the necessary changes to the question or answer.</li>
              <li>Click the 'Save' button to update the Q&A pair.</li>
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Testing the AI */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">3. Testing the AI</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The 'Test the AI' feature allows you to interact with the AI and ensure it responds 
              correctly to customer questions. To test the AI, follow these steps:
            </Typography>
            <Typography component="ol" sx={{ pl: 2 }}>
              <li>Click on the 'Test the AI' button on the dashboard.</li>
              <li>Type a question into the input field and press 'Enter'.</li>
              <li>Review the AI's response to ensure it matches the expected answer.</li>
              <li>If the AI's response is incorrect, you can update the Q&A pairs and test again.</li>
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Viewing Customer Conversations */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">4. Viewing Customer Conversations</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The 'Customer Conversations' section allows you to view interactions with customers who 
              have missed calls. To access this section, click on 'Customer Conversations' in the dashboard 
              menu. You will see a list of conversations, including the customer's question and the AI's response.
            </Typography>
            <Typography>
              You can use this section to monitor customer interactions and ensure the AI is providing 
              accurate and helpful responses.
            </Typography>
          </AccordionDetails>
        </Accordion>

        {/* Adding New Q&A Pairs */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">5. Adding New Q&A Pairs</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              To add new Q&A pairs to train the AI, follow these steps:
            </Typography>
            <Typography component="ol" sx={{ pl: 2 }}>
              <li>Navigate to the 'Q&A Pairs' section on the dashboard.</li>
              <li>Click on the 'Add New Q&A Pair' button.</li>
              <li>Enter the question in the 'Question' field.</li>
              <li>Enter the corresponding answer in the 'Answer' field.</li>
              <li>Click the 'Save' button to add the new Q&A pair.</li>
            </Typography>
            <Typography>
              The new Q&A pair will be added to the list and used by the AI to respond to customer questions.
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        {/* Business Settings */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">6. Managing Business Settings</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The 'Settings' page allows you to update your business information that is used in communications 
              with customers. To access your business settings:
            </Typography>
            <Typography component="ol" sx={{ pl: 2 }}>
              <li>Click on the Settings icon (gear) in the top-right corner of the dashboard.</li>
              <li>Update your business name, phone number, email, website, and description as needed.</li>
              <li>Click 'Save Changes' to update your information.</li>
            </Typography>
            <Typography>
              Keeping your business information up to date ensures accurate communication with your customers.
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        {/* Analytics & Reporting */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">7. Understanding Analytics</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography paragraph>
              The dashboard provides key analytics about your missed call handling performance:
            </Typography>
            <Typography component="ul" sx={{ pl: 2 }}>
              <li><strong>Missed Calls:</strong> Total number of missed calls received in the last 24 hours.</li>
              <li><strong>Auto-Texts:</strong> Number of automated text responses sent to customers.</li>
              <li><strong>Response Rate:</strong> Percentage of customer satisfaction based on interactions.</li>
              <li><strong>Average Response Time:</strong> How quickly your system responds to customer inquiries.</li>
            </Typography>
            <Typography>
              Use these metrics to monitor the effectiveness of your automated response system and make improvements as needed.
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="outlined" 
            color="primary"
            onClick={handleBack}
          >
            Return to Dashboard
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default HelpCenter; 